import React, { useState, useEffect, useMemo } from 'react';
import { formatDeNumber, formatTime } from '@/lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  CreditCard,
  Plus,
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  List,
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../../components/layout/Layout';
import RecordNavigator from '../../components/ui/record-navigator';
import SearchBuilder, { applyFilters } from '../../components/search/SearchBuilder';
import useTableColumns from '@/hooks/useTableColumns';
import useTableSort from '@/hooks/useTableSort';
import SortableTh from '@/components/table/SortableTh';
import api from '@/lib/axios';
import { formatDate } from '@/lib/utils';
import DateInput from '@/components/ui/date-input';
import { remainingHoursLabel, cardStandLabel } from '@/lib/prepaidCards';

// Define filterable fields
const FILTER_FIELDS = [
  { key: 'name', label: 'Bezeichnung', type: 'text' },
  { key: 'student_name', label: 'Schüler', type: 'text' },
  { key: 'teacher_name', label: 'Lehrer', type: 'text' },
  { key: 'status', label: 'Status', type: 'select', options: [
    { value: 'active', label: 'Laufend' },
    { value: 'completed', label: 'Abgeschlossen' },
    { value: 'cancelled', label: 'Abgebrochen' },
    { value: 'expired', label: 'Abgelaufen' },
  ]},
  { key: 'total_units', label: 'Einheiten (Gesamt)', type: 'number' },
  { key: 'remaining_units', label: 'Verbleibend', type: 'number' },
];

// Define columns
const DEFAULT_COLUMNS = [
  { key: 'name', label: 'Bezeichnung', visible: true },
  { key: 'student', label: 'Schüler', visible: true },
  { key: 'teacher', label: 'Lehrer', visible: true },
  { key: 'units', label: 'Stand', visible: true },
  { key: 'duration', label: 'Dauer', visible: true },
  { key: 'status', label: 'Status', visible: true },
  { key: 'dates', label: 'Termine', visible: true },
];

// „Stand“ sortiert nach verbleibenden Einheiten — danach fragt man beim Blick
// auf eine Guthabenkarte.
const CARD_SORT_ACCESSORS = {
  name: (c) => c.name,
  student: (c) => c.student_name,
  teacher: (c) => c.teacher_name,
  units: (c) => Number(c.remaining_units || 0),
  duration: (c) => Number(c.unit_duration_minutes || 0),
  status: (c) => c.status,
  dates: (c) => c.first_usage_date,
};

const PrepaidCardsPage = () => {
  const [allCards, setAllCards] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState([]);
  const { columns, setColumns } = useTableColumns('prepaid-cards-list', DEFAULT_COLUMNS);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUseDialog, setShowUseDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [usages, setUsages] = useState([]);
  const [usagesLoading, setUsagesLoading] = useState(false);
  
  // Form data
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    student_id: '',
    teacher_id: '',
    subject_id: '',
    total_units: 8,
    unit_duration_minutes: 30,
    total_price: 0,
    valid_from: '',
    valid_until: '',
  });
  
  const [useForm, setUseForm] = useState({
    usage_date: new Date().toISOString().split('T')[0],
    usage_time: '',
    units_used: 1,
    notes: '',
  });

  useEffect(() => {
    fetchData();
    fetchMasterData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await api.get('/prepaid-cards/stats');
      setStats(statsRes.data);
      
      // Load all cards
      let allData = [];
      let offset = 0;
      const batchSize = 100;
      
      while (true) {
        const response = await api.get(`/prepaid-cards?limit=${batchSize}&offset=${offset}`);
        const batch = response.data;
        if (!batch || batch.length === 0) break;
        allData = [...allData, ...batch];
        if (batch.length < batchSize) break;
        offset += batchSize;
      }
      
      setAllCards(allData);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterData = async () => {
    try {
      const [studentsRes, teachersRes, subjectsRes] = await Promise.all([
        api.get('/students?limit=100'),
        api.get('/teachers?limit=100'),
        api.get('/subjects'),
      ]);
      setStudents(studentsRes.data || []);
      setTeachers(teachersRes.data || []);
      setSubjects(subjectsRes.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Stammdaten:', error);
    }
  };

  // Apply filters client-side
  const { sort, toggleSort, sortRows } = useTableSort(CARD_SORT_ACCESSORS, { key: 'name' });

  const filteredCards = useMemo(() => {
    const searchFields = ['name', 'student_name', 'teacher_name'];
    return sortRows(applyFilters(allCards, filters, searchTerm, searchFields));
  }, [allCards, filters, searchTerm, sortRows]);

  const handleNavigate = (newIndex) => {
    setCurrentIndex(newIndex);
    const row = document.querySelector(`[data-testid="card-row-${filteredCards[newIndex]?.id}"]`);
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      row.classList.add('bg-blue-50');
      setTimeout(() => row.classList.remove('bg-blue-50'), 2000);
    }
  };

  const createCard = async () => {
    try {
      const data = {
        ...createForm,
        subject_id: createForm.subject_id ? parseInt(createForm.subject_id) : null,
        valid_from: createForm.valid_from || null,
        valid_until: createForm.valid_until || null,
      };
      
      await api.post('/prepaid-cards', data);
      toast.success('Prepaid-Karte erstellt');
      setShowCreateDialog(false);
      setCreateForm({
        name: '',
        description: '',
        student_id: '',
        teacher_id: '',
        subject_id: '',
        total_units: 8,
        unit_duration_minutes: 30,
        total_price: 0,
        valid_from: '',
        valid_until: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const recordUsage = async () => {
    if (!selectedCard) return;
    
    try {
      await api.post(`/prepaid-cards/${selectedCard.id}/use`, {
        ...useForm,
        prepaid_card_id: selectedCard.id,
      });
      toast.success('Einheit verbucht');
      setShowUseDialog(false);
      setSelectedCard(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Verbuchen');
    }
  };

  const openUseDialog = (card) => {
    setSelectedCard(card);
    setUseForm({
      usage_date: new Date().toISOString().split('T')[0],
      usage_time: '',
      units_used: 1,
      notes: '',
    });
    setShowUseDialog(true);
  };

  const openHistoryDialog = async (card) => {
    setSelectedCard(card);
    setUsages([]);
    setShowHistoryDialog(true);
    setUsagesLoading(true);
    try {
      const res = await api.get(`/prepaid-cards/${card.id}/usages`);
      setUsages(res.data || []);
    } catch (error) {
      toast.error('Verlauf konnte nicht geladen werden');
      setUsages([]);
    } finally {
      setUsagesLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { label: 'Laufend', icon: Play, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      completed: { label: 'Abgeschlossen', icon: CheckCircle2, className: 'bg-blue-50 text-blue-700 border-blue-200' },
      cancelled: { label: 'Abgebrochen', icon: XCircle, className: 'bg-red-50 text-red-700 border-red-200' },
      expired: { label: 'Abgelaufen', icon: Clock, className: 'bg-amber-50 text-amber-700 border-amber-200' },
    };
    const config = variants[status] || variants.active;
    const Icon = config.icon;
    return (
      <Badge className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const isColumnVisible = (key) => {
    const col = columns.find(c => c.key === key);
    return col ? col.visible : true;
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="prepaid-cards-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1">Prepaid-Karten</h1>
            <p className="text-slate-600">
              {filteredCards.length} von {allCards.length} gebuchten Karten — Stand aus Anwesenheitslisten
            </p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-slate-900 text-white hover:bg-slate-800"
            data-testid="create-card-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neue Karte
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Gesamt</p>
                    <p className="text-2xl font-bold">{stats.total_cards}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Play className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Aktiv</p>
                    <p className="text-2xl font-bold text-emerald-600">{stats.active_cards}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Abgeschlossen</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.completed_cards}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-red/10 rounded-lg">
                    <Users className="w-5 h-5 text-brand-red" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Einheiten verbraucht</p>
                    <p className="text-2xl font-bold">
                      {stats.total_units_used}/{stats.total_units_available}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="prepaid-stats-remaining">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Clock className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Einheiten übrig</p>
                    <p className="text-2xl font-bold text-emerald-700">
                      {stats.remaining_units ?? Math.max(0, (stats.total_units_available || 0) - (stats.total_units_used || 0))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* SearchBuilder */}
        <SearchBuilder
          fields={FILTER_FIELDS}
          onFilterChange={setFilters}
          onSearch={setSearchTerm}
          columns={columns}
          onColumnsChange={setColumns}
          placeholder="Karten suchen (Bezeichnung, Schüler, Lehrer)..."
          className="mb-4"
        />

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-600">
              {filteredCards.length} Ergebnisse
            </span>
            
            {filteredCards.length > 0 && (
              <RecordNavigator
                currentIndex={currentIndex}
                totalCount={filteredCards.length}
                onNavigate={handleNavigate}
                records={filteredCards}
                getRecordLabel={(card) => card.name}
              />
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="cards-table">
              <thead className="bg-slate-50 text-slate-500 font-medium text-xs uppercase tracking-wider border-b border-slate-200">
                <tr>
                  {DEFAULT_COLUMNS.filter((col) => isColumnVisible(col.key)).map((col) => (
                    <SortableTh
                      key={col.key}
                      sortKey={col.key}
                      sort={sort}
                      onSort={toggleSort}
                      testIdPrefix="cards-sort"
                    >
                      {col.label}
                    </SortableTh>
                  ))}
                  <th className="p-4 text-left">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-slate-500">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredCards.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-slate-500">
                      Keine Prepaid-Karten gefunden
                    </td>
                  </tr>
                ) : (
                  filteredCards.map((card) => (
                    <tr 
                      key={card.id} 
                      data-testid={`card-row-${card.id}`}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      {isColumnVisible('name') && (
                        <td className="p-4 text-sm text-slate-700 font-medium">{card.name}</td>
                      )}
                      {isColumnVisible('student') && (
                        <td className="p-4 text-sm text-slate-600">{card.student_name || '-'}</td>
                      )}
                      {isColumnVisible('teacher') && (
                        <td className="p-4 text-sm text-slate-600">{card.teacher_name || '-'}</td>
                      )}
                      {isColumnVisible('units') && (
                        <td className="p-4 text-sm" data-testid={`card-stand-${card.id}`}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium">
                              {card.used_units}/{card.total_units}
                            </span>
                            <div className="w-20 bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-brand-red h-2 rounded-full"
                                style={{ width: `${card.usage_percentage}%` }}
                              />
                            </div>
                          </div>
                          <p className="text-xs text-slate-600 mt-1">
                            {cardStandLabel(card)}
                          </p>
                          <p className="text-xs font-medium text-slate-800" data-testid={`card-remaining-${card.id}`}>
                            {remainingHoursLabel(card.remaining_units, card.unit_duration_minutes)}
                          </p>
                        </td>
                      )}
                      {isColumnVisible('duration') && (
                        <td className="p-4 text-sm text-slate-600">{card.unit_duration_minutes} Min</td>
                      )}
                      {isColumnVisible('status') && (
                        <td className="p-4 text-sm">{getStatusBadge(card.status)}</td>
                      )}
                      {isColumnVisible('dates') && (
                        <td className="p-4 text-sm text-slate-600">
                          {card.first_usage_date && (
                            <div className="text-xs">
                              <span className="text-slate-400">Erster:</span> {formatDate(card.first_usage_date)}
                            </div>
                          )}
                          {card.last_usage_date && (
                            <div className="text-xs">
                              <span className="text-slate-400">Letzter:</span> {formatDate(card.last_usage_date)}
                            </div>
                          )}
                          {!card.first_usage_date && <span className="text-slate-400">-</span>}
                        </td>
                      )}
                      <td className="p-4 text-sm">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openHistoryDialog(card)}
                            data-testid={`card-history-${card.id}`}
                          >
                            <List className="w-3 h-3 mr-1" />
                            Verlauf
                          </Button>
                          {card.status === 'active' && card.remaining_units > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openUseDialog(card)}
                              data-testid={`use-card-${card.id}`}
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Einheit buchen
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Neue Prepaid-Karte</DialogTitle>
              <DialogDescription>
                Erstellen Sie eine neue Guthabenkarte für Einzelunterricht
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label>Bezeichnung *</Label>
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                  placeholder="z.B. 8er Karte Bass 30 Min"
                />
              </div>
              
              <div>
                <Label>Schüler *</Label>
                <Select 
                  value={createForm.student_id} 
                  onValueChange={(v) => setCreateForm({...createForm, student_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Schüler auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.first_name} {s.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Lehrer</Label>
                <Select 
                  value={createForm.teacher_id} 
                  onValueChange={(v) => setCreateForm({...createForm, teacher_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Lehrer auswählen (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {teachers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.first_name} {t.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Fach</Label>
                <Select 
                  value={createForm.subject_id} 
                  onValueChange={(v) => setCreateForm({...createForm, subject_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Fach auswählen (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Einheiten</Label>
                  <div className="flex gap-1 mb-2">
                    {[4, 8, 10].map((n) => (
                      <Button
                        key={n}
                        type="button"
                        variant={createForm.total_units === n ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCreateForm({ ...createForm, total_units: n, name: createForm.name || `${n}er Karte` })}
                        data-testid={`create-units-${n}`}
                      >
                        {n}er
                      </Button>
                    ))}
                  </div>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={createForm.total_units}
                    onChange={(e) => setCreateForm({...createForm, total_units: parseInt(e.target.value) || 8})}
                  />
                </div>
                <div>
                  <Label>Dauer (Min)</Label>
                  <Select 
                    value={String(createForm.unit_duration_minutes)} 
                    onValueChange={(v) => setCreateForm({...createForm, unit_duration_minutes: parseInt(v)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 Min</SelectItem>
                      <SelectItem value="45">45 Min</SelectItem>
                      <SelectItem value="60">60 Min</SelectItem>
                      <SelectItem value="90">90 Min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Gesamtpreis (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={createForm.total_price}
                  onChange={(e) => setCreateForm({...createForm, total_price: parseFloat(e.target.value) || 0})}
                />
                {createForm.total_price > 0 && createForm.total_units > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    = {formatDeNumber(createForm.total_price / createForm.total_units)} € pro Einheit
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Gültig ab</Label>
                  <DateInput
                    
                    value={createForm.valid_from}
                    onChange={(e) => setCreateForm({...createForm, valid_from: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Gültig bis</Label>
                  <DateInput
                    
                    value={createForm.valid_until}
                    onChange={(e) => setCreateForm({...createForm, valid_until: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Beschreibung</Label>
                <Input
                  value={createForm.description}
                  onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                  placeholder="Optionale Beschreibung"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={createCard}
                disabled={!createForm.name || !createForm.student_id}
              >
                Erstellen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Use Dialog */}
        <Dialog open={showUseDialog} onOpenChange={setShowUseDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Einheit buchen</DialogTitle>
              <DialogDescription>
                {selectedCard && (
                  <span>
                    {selectedCard.name} - {selectedCard.remaining_units} Einheiten verbleibend
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Datum *</Label>
                <DateInput
                  
                  value={useForm.usage_date}
                  onChange={(e) => setUseForm({...useForm, usage_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Uhrzeit</Label>
                <Input
                  type="time"
                  value={formatTime(useForm.usage_time) || ''}
                  onChange={(e) => setUseForm({...useForm, usage_time: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Einheiten</Label>
                <Select 
                  value={String(useForm.units_used)} 
                  onValueChange={(v) => setUseForm({...useForm, units_used: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].filter(n => n <= (selectedCard?.remaining_units || 1)).map((n) => (
                      <SelectItem key={n} value={String(n)}>{n} Einheit{n > 1 ? 'en' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Notizen</Label>
                <Input
                  value={useForm.notes}
                  onChange={(e) => setUseForm({...useForm, notes: e.target.value})}
                  placeholder="Optionale Notizen"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUseDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={recordUsage}
                disabled={!useForm.usage_date}
              >
                Buchen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
          <DialogContent className="max-w-lg" data-testid="card-history-dialog">
            <DialogHeader>
              <DialogTitle>Kartenstand</DialogTitle>
              <DialogDescription>
                {selectedCard && (
                  <span>
                    {selectedCard.name} — {remainingHoursLabel(selectedCard.remaining_units, selectedCard.unit_duration_minutes)}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            {selectedCard && (
              <p className="text-sm text-slate-600" data-testid="card-history-stand">
                {cardStandLabel(selectedCard)}
              </p>
            )}
            {usagesLoading ? (
              <p className="text-sm text-slate-500">Verlauf wird geladen…</p>
            ) : usages.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="card-history-empty">Noch keine Einheiten aus Anwesenheitslisten verbucht</p>
            ) : (
              <ul className="space-y-2 max-h-64 overflow-y-auto" data-testid="card-history-list">
                {usages.map((u) => (
                  <li key={u.id} className="flex justify-between gap-3 text-sm border border-slate-100 rounded-md px-3 py-2">
                    <span>{formatDate(u.usage_date)}{u.notes ? ` · ${u.notes}` : ''}</span>
                    <span className="font-medium shrink-0">{u.units_used} Einheit{u.units_used === 1 ? '' : 'en'}</span>
                  </li>
                ))}
              </ul>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
                Schließen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PrepaidCardsPage;
